/* ============================================================
   EXON ANNOUNCEMENT BAR ISLAND — one strip, many sources.

   The bar is a shared surface: the section's own settings (ambient),
   the placement engine (campaign) and a customer's drop session
   (entitled) all want it. Rather than let each source reach in, every
   source REGISTERS an item and this island decides what is on screen.

   Deliberately NOT hung off exon-store.js: exon-store owns overlays and
   chrome, while the bar must stay one section plus its own assets so it
   can be ported to the other theme mechanically.

   Load order cannot matter — window.EXON.bar starts life as {q: []} and
   sources push register/withdraw operations onto that queue, which this
   island drains when it installs.

   An item's renderable content is an ORDERED list of ELEMENTS, capped at
   four, drawn from a closed versioned vocabulary. There is no `kind`: what
   used to be a kind is now a list, so changing what a drop shows is a change
   to data rather than to this renderer.

   Three stages, and the split is the point:
   · validate(item) is PURE — band and source rules, the cap, the
     unknown-required check. Every element is REQUIRED by default: an
     unknown or unrenderable required element skips the WHOLE item, never
     half a strip. Only an element marked `optional` degrades on its own.
     That is what keeps the vocabulary forward-compatible — a new element
     type reaching an old client kills that item cleanly.
   · compose(items, nowMs) is PURE — no DOM, no globals, no clock of its
     own. Unchanged by the move to elements.
   · the renderer holds the ONLY mutable state. General offers roll through
     the composed set; a customer's own entitled item pins the strip.

   The roll uses the approved 4200ms dwell and does not change the composer.
   ROTATION PINNING IS A PROPERTY OF BAND, never of element content: a timed
   general promotion rotates like any other. If a timer could pin the slot,
   every source would start carrying one to win it and the bands would stop
   being policy.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropsBarInit) return;
  window.__exonDropsBarInit = true;
  window.EXON = window.EXON || {};

  /* entitled > campaign > ambient */
  var BAND_RANK = { entitled: 3, campaign: 2, ambient: 1 };

  /* ------------------------------------------------------------
     THE ELEMENT VOCABULARY — closed and versioned. It grows only by design
     amendment with a typed payload; "things like that" is not a type.

       renderable  this client can draw it. A known type with no renderer
                   yet behaves exactly like an unknown one.
       optDefault  `optional` when the source does not say
       sources     the element is valid ONLY on these item sources, and
                   appearing anywhere else REJECTS THE WHOLE ITEM
       bands       valid only on these bands; elsewhere the element is
                   ignored and the item still renders
       palette     the value must name a theme token, never a colour
     ------------------------------------------------------------ */
  var ELEMENTS = {
    headline:  { renderable: true,  optDefault: false },
    highlight: { renderable: true,  optDefault: false },
    /* carries NO time of its own — it renders the ITEM's window.end_at, so a
       timer can never disagree with the visibility gate that hides the item */
    timer:     { renderable: true,  optDefault: false },
    /* BAND-LOCKED to session items. The server already strips codes from
       anything but an entitled payload; this makes it impossible for a future
       engine or settings source to ship one at all. */
    code:      { renderable: true,  optDefault: false, sources: { session: 1 } },
    /* The existing section and engine use the same bounded link element. */
    cta:       { renderable: true, optDefault: true },
    /* the SUPPORTING LINE under the headline (poster bar, 2026-09-21).
       Optional by default so an older client that never learned it still
       shows the item without it, never skips it. */
    subline:   { renderable: true,  optDefault: true },
    /* the campaign's KEY ART, masked into the glass. Optional; the src is
       admitted only from the store's own CDN (see safeArt). */
    art:       { renderable: true,  optDefault: true },
    /* The DROP treatment is reserved: entitled items always render it and
       ignore any accent, so accent is meaningless above the campaign band.
       An unknown token degrades to the default treatment — it never skips
       the item. Stamped on the shell as [data-bar-accent]; not a drawn zone. */
    accent:    { renderable: true,  optDefault: true, bands: { campaign: 1, ambient: 1 }, palette: 'ACCENT_TOKENS' }
  };
  /* the named accent palette — the six campaign colours of the poster bar.
     Amber is deliberately absent: it means "your drop" and only the entitled
     band wears it. Gold is absent: reserved for Vault editions. */
  var ACCENT_TOKENS = { lime: 1, cyan: 1, magenta: 1, purple: 1, mint: 1, pearl: 1 };
  /* the cap counts DRAWN elements only — highlight, headline, subline, timer,
     cta. Accent and art are stamped on the shell and cost the strip nothing;
     code is never drawn on the strip at all (it rides the item for the
     ticket). None of the three is counted, so a member's item may carry its
     code beside all five drawn zones. */
  var MAX_ELEMENTS = 5;
  var UNCOUNTED_TYPES = { accent: 1, art: 1, code: 1 };

  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function rank(band) { return BAND_RANK[band] || 0; }

  /* PURE, and deliberately unambiguous: a timestamp is either an ISO string
     (what the API sends) or an epoch in MILLISECONDS, the same unit as the
     nowMs it is compared against. No seconds-vs-ms guess — a heuristic in a
     pure function makes its own behaviour depend on the magnitude of its
     input, which is exactly what a composer must not do. */
  function parseTime(v) {
    if (v == null || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    var t = Date.parse(v);
    return isNaN(t) ? null : t;
  }

  /* ============================================================
     validate — PURE. Runs BEFORE compose. Returns the item with its element
     list resolved, or null when the item must be skipped entirely.

     Reads no clock: whether a window has PASSED is compose's business, and
     the only thing checked here is that a timer has something to count.
     ============================================================ */
  function validate(item) {
    if (!item || typeof item !== 'object') return null;
    if (typeof item.id !== 'string' || !item.id) return null;
    /* an unrankable band cannot be ordered against anything */
    if (!rank(item.band)) return null;

    var raw = item.elements;
    if (!Array.isArray(raw)) return null;         /* elements IS the contract */
    var drawn = 0;
    for (var c = 0; c < raw.length; c++) if (!(raw[c] && has(UNCOUNTED_TYPES, raw[c].type))) drawn++;
    if (drawn > MAX_ELEMENTS) return null;        /* the cap */

    var itemEnd = item.window ? parseTime(item.window.end_at) : null;
    var out = [];
    var seen = {};

    for (var i = 0; i < raw.length; i++) {
      var e = raw[i];
      /* a malformed entry is required-by-default, so it takes the item */
      if (!e || typeof e !== 'object') return null;
      var type = (typeof e.type === 'string') ? e.type : '';
      var spec = has(ELEMENTS, type) ? ELEMENTS[type] : null;
      var optional = (e.optional === undefined) ? !!(spec && spec.optDefault) : (e.optional === true);

      /* UNKNOWN type — the whole point of the skip rule. A new element type
         reaching an old client kills that item cleanly instead of drawing
         half of it. */
      if (!spec) { if (optional) continue; return null; }

      /* a source lock is a leak guard, so violating it takes the ITEM. An OWN
         key only: 'toString' or 'constructor' must not pass on the prototype */
      if (spec.sources && !has(spec.sources, item.source)) return null;

      /* a band lock is precedence, not safety: the element is simply not
         meaningful here, so it is dropped and the item stands */
      if (spec.bands && !spec.bands[item.band]) continue;

      /* a named token that is not in the palette degrades to the default
         treatment — explicitly NOT a skip */
      if (spec.palette && !has(ACCENT_TOKENS, text(e.token))) continue;

      /* one of each type: two headlines have no defined order of precedence */
      if (seen[type]) return null;

      /* a required element this client cannot draw behaves exactly like an
         unknown one — an optional (decorative) one degrades alone */
      if (!spec.renderable) { if (optional) continue; return null; }

      var el = normalizeElement(type, e, optional);
      if (type === 'cta') {
        el.href = safeHref(el.href, item.source);
        el.empty = !(el.label && el.href);
      }
      /* a timer with nothing to count is not renderable content */
      if (type === 'timer' && itemEnd === null) { if (optional) continue; return null; }
      /* neither is an empty string where the payload IS the content */
      if (el.empty) { if (optional) continue; return null; }

      seen[type] = 1;
      out.push(el);
    }

    /* an item that would paint nothing cannot hold the strip */
    if (!out.length) return null;

    return {
      id: item.id,
      source: item.source,
      trigger: item.trigger,
      band: item.band,
      elements: out,
      a11y_label: item.a11y_label,
      window: item.window,
      facts: item.facts
    };
  }

  function normalizeElement(type, e, optional) {
    var el = { type: type, optional: optional, empty: false };
    if (type === 'headline') { el.text = text(e.text); el.empty = !el.text; }
    else if (type === 'highlight') { el.pct = text(e.pct); el.empty = !el.pct; }
    else if (type === 'code') { el.value = text(e.value); el.empty = !el.value; }
    else if (type === 'cta') { el.label = text(e.label); el.href = text(e.href); el.empty = !(el.label && el.href); }
    else if (type === 'accent') { el.token = text(e.token); el.empty = !el.token; }
    else if (type === 'subline') { el.text = text(e.text); el.empty = !el.text; }
    else if (type === 'art') { el.src = safeArt(e.src); el.empty = !el.src; }
    /* the timer's optional LABEL ("closes in" / "opens in") — the figure itself
       still comes from the item's window, never from the element */
    else if (type === 'timer') { if (e.label) el.label = text(e.label); }
    return el;
  }

  /* ============================================================
     compose — PURE. Same items + same nowMs => same array, always.
     ============================================================ */
  function compose(items, nowMs) {
    var arr = Array.isArray(items) ? items : [];
    var live = [];
    var i, it, k;

    for (i = 0; i < arr.length; i++) {
      it = arr[i];
      if (!it || typeof it !== 'object') continue;
      if (typeof it.id !== 'string' || !it.id) continue;
      /* 1 — element-level skipping moved UP into validate(), which runs
         first. The band guard stays: compose must be a total order even when
         called directly with something unvalidated. */
      if (!rank(it.band)) continue;
      /* 2 — outside its scheduled window */
      var start = it.window ? parseTime(it.window.start_at) : null;
      var end = it.window ? parseTime(it.window.end_at) : null;
      if ((start !== null && start > nowMs) || (end !== null && end <= nowMs)) continue;
      live.push(it);
    }

    /* 3 — dedup by trigger, keeping the highest band: an entitled session
       item for campaign X suppresses a general item advertising X. Different
       triggers are never suppressed — they compete in the sort below. A null
       or empty trigger means "no commercial event" (ambient) and suppresses
       nothing, so those never collapse into each other. */
    var byTrigger = {};
    var kept = [];
    for (i = 0; i < live.length; i++) {
      it = live[i];
      var trig = (it.trigger == null || it.trigger === '') ? null : String(it.trigger);
      if (trig === null) { kept.push(it); continue; }
      k = 't:' + trig;
      var prev = byTrigger[k];
      if (!prev) { byTrigger[k] = it; continue; }
      if (rank(it.band) > rank(prev.band)) { byTrigger[k] = it; continue; }
      /* same band, same trigger: settle it with the same total order used
         below, so the survivor never depends on input order */
      if (rank(it.band) === rank(prev.band) && order(it, prev) < 0) byTrigger[k] = it;
    }
    for (k in byTrigger) if (has(byTrigger, k)) kept.push(byTrigger[k]);

    /* 4 — band, then newest window start, then id. A TOTAL order (ids are
       unique), so two equal candidates can never flicker between renders. */
    kept.sort(order);
    return kept;
  }

  function order(a, b) {
    var d = rank(b.band) - rank(a.band);
    if (d) return d;
    var sa = a.window ? parseTime(a.window.start_at) : null;
    var sb = b.window ? parseTime(b.window.start_at) : null;
    if (sa === null) sa = -Infinity;
    if (sb === null) sb = -Infinity;
    if (sa !== sb) return sb - sa; /* start_at DESCENDING */
    return a.id < b.id ? -1 : (a.id > b.id ? 1 : 0); /* id ASCENDING */
  }

  /* ============================================================
     registry — register(item) updates in place by id; both ops idempotent
     ============================================================ */
  var registry = {};

  function text(v) { return (v == null) ? '' : String(v); }

  /* the registry stores items as REGISTERED — validation is a pipeline stage
     in render(), not a gate at the door, so validate() stays a pure function
     over whatever a source actually sent */
  function normalize(item) {
    if (!item || typeof item !== 'object') return null;
    var id = text(item.id);
    if (!id) return null;
    var w = item.window;
    return {
      id: id,
      source: text(item.source),
      trigger: (item.trigger == null || item.trigger === '') ? null : String(item.trigger),
      band: text(item.band),
      elements: Array.isArray(item.elements) ? item.elements.slice() : null,
      a11y_label: text(item.a11y_label),
      window: (w && typeof w === 'object')
        ? { start_at: w.start_at == null ? null : w.start_at, end_at: w.end_at == null ? null : w.end_at }
        : null,
      /* facts are opaque to the strip: carried, never drawn, so the element
         vocabulary stays closed. Only a plain object rides along. */
      facts: (item.facts && typeof item.facts === 'object') ? item.facts : null
    };
  }

  function register(item) {
    var norm = normalize(item);
    if (!norm) return;
    registry[norm.id] = norm;
    schedule();
  }

  function withdraw(id) {
    var key = text(id);
    if (key && has(registry, key)) delete registry[key];
    schedule();
  }

  function values() {
    var out = [];
    for (var k in registry) if (has(registry, k)) out.push(registry[k]);
    return out;
  }

  /* ============================================================
     renderer — the only mutable state in the island
     ============================================================ */
  var curId = null;
  var curBand = null;
  var curApplied = false;
  /* the rendered element types, space-joined — replaces curKind */
  var curTypes = '';
  var queued = false;
  var ticker = null;
  var rollTimer = null;
  var rollForId = null;
  var rollHovered = false;
  var rollFocused = false;
  var rollPaused = false;
  var rollAnimations = [];
  var rollGhost = null;
  var ROLL_DWELL_MS = 4200;
  /* an item that carries a supporting line is READ, not glanced at */
  var ROLL_DWELL_COPY_MS = 6000;
  var rollTouched = false;
  function dwellMs() { return hasType(curTypes, 'subline') ? ROLL_DWELL_COPY_MS : ROLL_DWELL_MS; }
  /* the shell has been taken out of layout for good — see blank() */
  var collapsed = false;
  /* the drops client has reached a terminal path; wired up at boot */
  var dropsSettled = false;
  /* the ambient blob was present and unreadable: hold the server-rendered
     content rather than replace it with nothing (see readAmbient) */
  var ssrHeld = false;
  var ssrSnapshot = null;
  /* HOLD: "the view must not change". Distinct from null, which means
     "there is nothing to show". */
  var HOLD = {};

  /* ------------------------------------------------------------
     THE EXPIRED TREATMENT — client presentation memory.

     Post-window the server strips the payload to {entitled:false}, so
     "expired" is a fact only the browser can hold (contract:
     ACCEPTED_FUTURE_CHANGES.client_presentation_memory). Two ways in, and
     only two:
       · an entitled session item that was LIVE on this page reaches its
         end_at while the page is open — the in-page transition;
       · a bounded storage marker, written only from a validated entitled
         item while it was live, keyed by campaign_version_id, read once
         on a later load whose session settles un-entitled.
     Either way it may select ONLY the expired treatment, and it says so
     ONCE (spec §1: "it says so once, then stops asking for attention"):
     the marker is consumed the first time it paints. An item that was
     already past its end when it arrived never earns it — that is plain
     absence, which is what the strip rule means.
     ------------------------------------------------------------ */
  var seenLive = {};
  var ended = null;
  var MEMORY_KEY = 'exon:drops:ended';
  var MEMORY_TTL_MS = 24 * 3600 * 1000;

  function memoryStores() {
    var out = [];
    try { if (window.localStorage) out.push(window.localStorage); } catch (e) { /* blocked */ }
    try { if (window.sessionStorage) out.push(window.sessionStorage); } catch (e) { /* blocked */ }
    return out;
  }
  function memoryRead() {
    var stores = memoryStores();
    for (var i = 0; i < stores.length; i++) {
      var raw = null;
      try { raw = stores[i].getItem(MEMORY_KEY); } catch (e) { raw = null; }
      if (!raw) continue;
      try {
        var m = JSON.parse(raw);
        if (m && typeof m === 'object' && m.v) return m;
      } catch (e) { /* unreadable is the same as absent */ }
    }
    return null;
  }
  function memoryWrite(m) {
    var raw;
    try { raw = JSON.stringify(m); } catch (e) { return; }
    var stores = memoryStores();
    for (var i = 0; i < stores.length; i++) {
      try { stores[i].setItem(MEMORY_KEY, raw); return; } catch (e) { /* full / private */ }
    }
  }
  function memoryClear() {
    var stores = memoryStores();
    for (var i = 0; i < stores.length; i++) {
      try { stores[i].removeItem(MEMORY_KEY); } catch (e) { /* nothing to undo */ }
    }
  }
  /* written from a validated, painted, entitled session item and nothing
     else. It carries the version id, the end, and the one element the
     expired strip still shows — the figure — so the treatment needs no
     wire and no second source of copy. */
  function remember(it) {
    if (!it || it.band !== 'entitled' || it.source !== 'session') return;
    var v = it.facts && it.facts.version_id;
    var end = it.window ? parseTime(it.window.end_at) : null;
    if (!v || end === null) return;
    var pct = '';
    for (var i = 0; i < it.elements.length; i++) {
      if (it.elements[i].type === 'highlight') pct = it.elements[i].pct;
    }
    var cur = memoryRead();
    if (cur && cur.v === v && cur.shown) return; /* already said once */
    if (cur && cur.v === v && cur.end === end && cur.pct === pct) return;
    memoryWrite({ v: v, end: end, pct: pct, shown: 0 });
  }
  /* the page-load half: a marker for a campaign that ended within the
     bound, not yet shown, when nothing entitled is on offer */
  function recallEnded(nowMs) {
    var m = memoryRead();
    if (!m || m.shown) return null;
    var end = parseTime(m.end);
    if (end === null || end > nowMs) return null;
    if (nowMs - end > MEMORY_TTL_MS) { memoryClear(); return null; }
    return {
      id: 'drops:ended:' + m.v,
      source: 'session',
      band: 'entitled',
      elements: m.pct ? [{ type: 'highlight', pct: m.pct }] : [],
      window: { start_at: null, end_at: m.end },
      facts: { version_id: m.v }
    };
  }
  function consume(it) {
    var v = it && it.facts && it.facts.version_id;
    var m = memoryRead();
    if (m && (!v || m.v === v)) { m.shown = 1; memoryWrite(m); }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    var run = function () { queued = false; render(); };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  function root() { return document.querySelector('[data-exon-annc]'); }
  function zone(el, name) { return el.querySelector('[data-bar-' + name + ']'); }

  /* Only a member entitlement pins the strip. Timer-bearing general
     promotions still participate in the same ordered rotation. */
  function pick(set, advance) {
    var top = set[0] || null;
    if (!top || !curId) return top;
    var current = -1;
    for (var i = 0; i < set.length; i++) if (set[i].id === curId) { current = i; break; }
    if (current < 0) return top;                     /* withdrawn or expired */
    var cur = set[current];
    if (curBand === 'entitled' && cur.band !== 'entitled') return HOLD;
    if (top.band === 'entitled' && cur.band !== 'entitled') return top;
    if (cur.band === 'entitled' || !advance) return cur;
    return set[(current + 1) % set.length];
  }

  function stopRoll() {
    if (rollTimer !== null) clearTimeout(rollTimer);
    rollTimer = null;
    rollForId = null;
  }

  function syncRoll(set) {
    var el = root();
    var canRoll = !!(el && !collapsed && !ended && curBand !== 'entitled' && set.length > 1);
    var control = el && zone(el, 'pause');
    if (control) {
      control.hidden = !canRoll;
      control.setAttribute('aria-pressed', rollPaused ? 'true' : 'false');
      var label = control.getAttribute(rollPaused ? 'data-resume-label' : 'data-pause-label');
      if (label) control.setAttribute('aria-label', label);
      control.setAttribute('data-paused', rollPaused ? 'true' : 'false');
    }
    if (!canRoll || rollPaused || rollHovered || rollFocused || rollTouched || document.hidden) { stopRoll(); return; }
    if (rollTimer !== null && rollForId === curId) return;
    stopRoll();
    rollForId = curId;
    rollTimer = setTimeout(function () { rollTimer = null; render(true); }, dwellMs());
  }

  function clearRollMotion() {
    for (var i = 0; i < rollAnimations.length; i++) rollAnimations[i].cancel();
    rollAnimations = [];
    if (rollGhost && rollGhost.parentNode) rollGhost.parentNode.removeChild(rollGhost);
    rollGhost = null;
  }

  /* The leaving copy is inert and inaccessible. The actual links and zones
     remain the same nodes, so event listeners and keyboard focus survive. */
  function startRollMotion(el) {
    var body = zone(el, 'body');
    if (!body || !body.animate || !body.cloneNode || !el.appendChild) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var ghost = body.cloneNode(true);
    ghost.removeAttribute('data-bar-body');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('inert', '');
    ghost.style.cssText = 'position:absolute;pointer-events:none;left:50%;top:50%;transform:translate(-50%,-50%);width:' + body.offsetWidth + 'px;';
    el.appendChild(ghost);
    rollGhost = ghost;
    var options = { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)' };
    var exit = ghost.animate([
      { transform: 'translate(-50%,-50%)', opacity: 1 },
      { transform: 'translate(-50%,-150%)', opacity: 0 }
    ], options);
    var enter = body.animate([
      { transform: 'translateY(100%)', opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 }
    ], options);
    rollAnimations = [exit, enter];
    exit.onfinish = function () { if (rollGhost === ghost) clearRollMotion(); };
  }

  function bindRoll() {
    var el = root();
    if (!el) return;
    el.addEventListener('mouseenter', function () { rollHovered = true; stopRoll(); });
    el.addEventListener('mouseleave', function () { rollHovered = false; render(); });
    el.addEventListener('focusin', function () { rollFocused = true; stopRoll(); });
    el.addEventListener('focusout', function () { rollFocused = false; render(); });
    /* a finger on the strip is a reader; the pause control is hidden on phones */
    el.addEventListener('touchstart', function () { rollTouched = true; stopRoll(); }, { passive: true });
    el.addEventListener('touchend', function () { rollTouched = false; render(); }, { passive: true });
    el.addEventListener('touchcancel', function () { rollTouched = false; render(); }, { passive: true });
    document.addEventListener('visibilitychange', function () { render(); });
    var control = zone(el, 'pause');
    if (control) control.addEventListener('click', function () { rollPaused = !rollPaused; render(); });
  }

  /* the pipeline: validate (pure) -> compose (pure) -> pick (member pin / roll) */
  function validated() {
    var raw = values();
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var v = validate(raw[i]);
      if (v) out.push(v);
    }
    return out;
  }

  function render(advance) {
    var el = root();
    if (!el) return;
    var now = Date.now();
    reap(now);
    var items = validated();
    /* the entitled session items that are live NOW — the only ones that may
       later earn the expired treatment */
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var end = it.window ? parseTime(it.window.end_at) : null;
      var start = it.window ? parseTime(it.window.start_at) : null;
      if (it.band === 'entitled' && it.source === 'session' && (start === null || start <= now) && end !== null && end > now) {
        seenLive[it.id] = it;
        remember(it);
      }
    }
    var set = compose(items, now);
    var next = pick(set, advance === true);
    if (next === HOLD) { retime(items, now); syncRoll(set); return; }
    if (!next || next.id !== curId || ended) {
      clearRollMotion();
      if (advance === true && next && next.band !== 'entitled' && !ended) startRollMotion(el);
    }
    /* a fresh entitled item outranks a memory of an old one */
    if (next && next.band === 'entitled') { ended = null; paint(el, next); }
    else if (ended) paintEnded(el, ended);
    else if (next) paint(el, next);
    else blank(el);
    retime(items, now);
    syncRoll(set);
  }

  /* An item past its end_at is filtered out of every future compose and can
     never come back — only a fresh register() could revive that id. Dropping
     it from the registry is what lets retime() see the truth and stop the
     interval; leaving it there kept a 1s timer alive for the rest of the page
     view on the strength of a countdown that had already finished.

     An entitled session item that was live on THIS page and ends here is the
     in-page transition: it leaves the registry like any other and becomes
     the one thing the expired treatment may show. */
  function reap(nowMs) {
    for (var k in registry) {
      if (!has(registry, k)) continue;
      var w = registry[k].window;
      var end = w ? parseTime(w.end_at) : null;
      if (end !== null && end <= nowMs) {
        if (has(seenLive, k)) {
          ended = seenLive[k];
          delete seenLive[k];
          /* the ticket's own clock may have stopped a beat before this reap
             ran; tell it the campaign closed so both surfaces turn together */
          try { document.dispatchEvent(new CustomEvent('exon:drops:ended')); } catch (e) { /* no consumer is load-bearing */ }
        }
        delete registry[k];
      }
    }
  }

  function setText(node, value) {
    if (!node) return;
    if (node.textContent !== value) node.textContent = value;
  }
  function setAttr(el, name, value) {
    if (value) { if (el.getAttribute(name) !== value) el.setAttribute(name, value); }
    else el.removeAttribute(name);
  }

  /* A bar item can arrive over the NETWORK, so a session- or engine-sourced
     CTA stays on this origin: root-relative paths only, never
     protocol-relative, never absolute. The `settings` source is the merchant's
     own theme setting and may legitimately point off-site — it is already
     trusted to write the headline sitting next to it. */
  function safeHref(h, source) {
    var v = text(h).trim();
    if (!v) return '';
    if (/[\s\\\u0000-\u001f\u007f]/.test(v)) return '';
    if (v === '/') return v;
    if (/^\/[^/\\]/.test(v)) return v;
    /* Only the public onboarding producer can supply this external CTA.
       Compact start tokens are exactly 48 base64url characters. */
    if (source === 'drops_public' && /^https:\/\/t\.me\/[A-Za-z][A-Za-z0-9_]{4,31}(?:\?start=[A-Za-z0-9_-]{48})?$/.test(v)) return v;
    if (source === 'settings' && /^https?:\/\/[^\s/\\]/i.test(v)) return v;
    return '';
  }

  /* Key art is painted as a CSS url() on the shell, so it is admitted from
     the store's own CDN only — whatever the source. A settings block always
     resolves there (image_url); anything else is not art for this strip. */
  function safeArt(src) {
    var v = text(src).trim();
    if (!v) return '';
    if (/[\s"'()\\\u0000-\u001f\u007f]/.test(v)) return '';
    if (/^https:\/\/cdn\.shopify\.com\/[^?#]+\.(png|jpe?g|webp|avif|gif)(\?[^#]*)?$/i.test(v)) return v;
    return '';
  }

  /* every zone the renderer owns; anything without an element is hidden */
  var RENDER_ZONES = ['highlight', 'headline', 'subline', 'code', 'timer', 'cta'];
  /* The STRIP never draws the code element (spec §1 has no code chip on any
     bar state). It rides the item for the ticket, which is where a customer
     copies it, and the source lock in validate() keeps guarding it there.
     So the strip's zone for it stays hidden and it is left out of
     [data-bar-elements], which names what is DRAWN. */
  var STRIP_SKIPS = { code: 1 };
  /* accent and art are NOT drawn zones: they are stamped on the shell
     (data-bar-accent, the art layer) and left out of [data-bar-elements] */
  var SHELL_ONLY = { accent: 1, art: 1 };

  function typesOf(it) {
    var out = [];
    for (var i = 0; i < it.elements.length; i++) out.push(it.elements[i].type);
    return out;
  }
  function drawnTypes(it) {
    var all = typesOf(it);
    var out = [];
    for (var i = 0; i < all.length; i++) if (!STRIP_SKIPS[all[i]] && !SHELL_ONLY[all[i]]) out.push(all[i]);
    return out;
  }
  /* the discount is on the order: the server's order_application record,
     carried on the item as a fact. The one selector of the APPLIED state. */
  function isApplied(it) {
    var f = it && it.facts;
    var n = f && f.applied_amount_agorot;
    return typeof n === 'number' && isFinite(n) && n >= 0;
  }

  function paint(el, it) {
    /* the shell is already out of layout; putting it back now would jump the
       page under someone who has been reading a settled one */
    if (collapsed) return;

    var announce = shouldAnnounce(it);
    var types = drawnTypes(it);
    var applied = isApplied(it);
    var byType = {};
    var i;
    for (i = 0; i < it.elements.length; i++) byType[it.elements[i].type] = it.elements[i];

    setAttr(el, 'data-bar-band', it.band);
    setAttr(el, 'data-bar-source', it.source);
    /* replaces data-bar-kind: the SAME hook power for CSS, at the granularity
       the contract now uses — the DRAWN element types, in order. */
    setAttr(el, 'data-bar-elements', types.join(' '));
    el.removeAttribute('data-bar-empty');
    /* APPLIED (spec §1): the tag takes the pip's seat, the closing line says
       the discount is saved until payment. Off the server fact only. */
    setAttr(el, 'data-bar-state', applied ? 'applied' : '');
    /* the campaign's colour and art ride the SHELL. An entitled item never
       carries an accent past validate() (band lock), so the drop keeps amber. */
    setAttr(el, 'data-bar-accent', byType.accent ? byType.accent.token : '');
    paintArt(el, byType.art ? byType.art.src : '');
    /* DIRECTION FOLLOWS THE COPY, not the page. A Hebrew campaign on the /en
       page (an English device, or the editor previewing English) must still
       read right-to-left; an English line on the Hebrew page reads
       left-to-right. The digits and the stamp stay LTR by their own rule. */
    paintDir(el, (byType.headline ? byType.headline.text : '') + ' ' + (byType.subline ? byType.subline.text : ''));

    for (i = 0; i < RENDER_ZONES.length; i++) {
      var name = RENDER_ZONES[i];
      var node = zone(el, name);
      if (!node) continue;
      var e = byType[name];
      if (!e || STRIP_SKIPS[name]) { clearZone(name, node); continue; }
      if (name === 'highlight') setText(node, e.pct);
      else if (name === 'headline') setText(node, e.text);
      else if (name === 'subline') setText(node, e.text);
      else if (name === 'cta') {
        setText(node, e.label);
        setAttr(node, 'href', e.href);
      }
      else if (name === 'timer') { paintTimer(node, it, Date.now(), e.label); continue; }
      node.hidden = false;
    }
    var saved = zone(el, 'saved');
    if (saved) saved.hidden = !applied;
    /* FINAL HOURS. Read off the RENDERED clock, never re-derived from end_at:
       a leading `00:` is exactly "under an hour" (a longer window has a
       non-zero hour field; a multi-day one carries its day count). The same
       predicate the ticket uses, so the two surfaces turn urgent together.
       No threshold constant, no enum — the contract forbids inventing one. */
    var tnode = byType.timer ? zone(el, 'timer') : null;
    var tval = tnode ? (tnode.querySelector && tnode.querySelector('[data-bar-timer-value]')) || tnode : null;
    if (tval && !tnode.hidden && /^00:/.test(tval.textContent || '')) el.setAttribute('data-bar-urgent', '');
    else el.removeAttribute('data-bar-urgent');
    var tag = zone(el, 'tag');
    if (tag) tag.hidden = !applied;

    /* ELEMENT ORDER IS CONTRACT — the list is ordered, so the DOM follows it.
       Which means changing what a drop leads with is a change to the preset
       that built the list, not to this renderer. */
    reorder(el, types);

    /* the LIVE pip is section chrome, not an element: it is shown while the
       drop runs and gives its seat to the tag once the discount is applied */
    var live = zone(el, 'live');
    if (live) live.hidden = applied;

    curId = it.id;
    curBand = it.band;
    curTypes = types.join(' ');
    curApplied = applied;

    if (announce) speak(it);
  }

  function clearZone(name, node) {
    if (name === 'timer') {
      /* the timer zone OWNS markup (the label span and the value node);
         wiping its textContent destroyed both, and the next timed item then
         painted a bare clock with no label. Clear the figure only. */
      var v = node.querySelector && node.querySelector('[data-bar-timer-value]');
      setText(v || node, '');
    }
    else if (name !== 'cta') setText(node, '');
    else { node.removeAttribute('href'); setText(node, ''); }
    node.hidden = true;
  }

  /* Move the zone nodes into element order. appendChild on an attached node
     moves it, so this is a reorder rather than a rebuild — and it runs only
     when the order actually changed, since re-appending costs layout.

     The pip, and the tag that takes its seat, LEAD the strip (spec §1: every
     bar state opens with them, which in RTL is the right-hand edge). They are
     appended first, then the content in element order; hidden zones are left
     where they are, in front, out of the flow.

     The hairline (.annc-sep) is chrome between the words and the clock /
     button, so it travels with them: it is appended once, immediately before
     the first timer or cta in the list. Left alone it ended up in FRONT of
     the pip after any reorder (EXO-4607 #2). A list with neither leaves it
     where it is - CSS hides it there. */
  var curOrder = '';
  var LEADING = ['live', 'tag'];
  /* a zone may sit inside a wrapper (the headline and the supporting line
     share [data-bar-txt]); what moves is the wrapper, the body's own child */
  function hostOf(node, body) {
    var n = node;
    while (n && n.parentNode && n.parentNode !== body) n = n.parentNode;
    return (n && n.parentNode === body) ? n : null;
  }
  function reorder(el, types) {
    var sig = types.join(' ');
    if (sig === curOrder) return;
    var body = zone(el, 'body') || el;
    if (!body.appendChild) return;
    var i, node;
    for (i = 0; i < LEADING.length; i++) {
      node = zone(el, LEADING[i]);
      if (node && node.parentNode === body) body.appendChild(node);
    }
    /* guarded: a harness DOM may have no querySelector on the body */
    var sep = body.querySelector ? body.querySelector('.annc-sep') : null;
    for (i = 0; i < types.length; i++) {
      if (sep && (types[i] === 'timer' || types[i] === 'cta')) {
        if (sep.parentNode === body) body.appendChild(sep);
        sep = null; /* once */
      }
      node = hostOf(zone(el, types[i]), body);
      if (node) body.appendChild(node);
    }
    /* the closing line of the applied state sits after the content */
    node = zone(el, 'saved');
    if (node && node.parentNode === body) body.appendChild(node);
    curOrder = sig;
  }

  function blank(el) {
    /* the ambient blob was unreadable: the server-rendered content is the only
       correct thing on the page and blanking it would be strictly worse than
       doing nothing */
    if (ssrHeld) { restoreSSR(el); return; }

    setAttr(el, 'data-bar-band', '');
    setAttr(el, 'data-bar-elements', '');
    setAttr(el, 'data-bar-source', '');
    for (var i = 0; i < RENDER_ZONES.length; i++) {
      var n = zone(el, RENDER_ZONES[i]);
      if (n) clearZone(RENDER_ZONES[i], n);
    }
    var live = zone(el, 'live');
    if (live) live.hidden = true;
    var tag = zone(el, 'tag');
    if (tag) tag.hidden = true;
    var saved = zone(el, 'saved');
    if (saved) saved.hidden = true;
    el.removeAttribute('data-bar-state');
    el.removeAttribute('data-bar-urgent');
    el.removeAttribute('data-bar-accent');
    paintArt(el, '');
    curId = null;
    curBand = null;
    curTypes = '';
    curApplied = false;

    /* Taking the shell out of layout moves the header, so it happens ONCE and
       only after drops have settled. Collapsing on the 4000ms pre-paint
       watchdog while a request was still in flight, then re-expanding when it
       answered, was a guaranteed jump — and "the height is always reserved" is
       the promise this whole surface is built on. While drops are resolving
       the shell stays open and empty; its content is hidden either way.

       The section stamps data-bar-empty server-side, and the CSS only holds
       that open for as long as [data-drops-pending] lasts — so leaving it
       there would collapse the shell on the watchdog, behind the island's
       back, however careful the island was. The attribute is therefore taken
       AWAY while the answer is still coming, and put back once and for all
       when it arrives. */
    if (dropsSettled) collapsed = true;
    if (collapsed) el.setAttribute('data-bar-empty', '');
    else el.removeAttribute('data-bar-empty');
  }

  /* THE EXPIRED STRIP (spec §1, "Expired"): the figure stays, the headline is
     the theme's own closing line (it rides the shell as data-bar-copy-expired
     so the copy lives in Liquid with the rest), the tag replaces the LIVE pip,
     and there is no clock — at zero it is gone, never negative. Painted at
     the entitled band, because it is a statement about the customer's own
     drop and an ambient promotion must not take the strip back from under
     it; the next page load is where the strip returns to normal. */
  function paintEnded(el, it) {
    if (collapsed) return;
    var pct = '';
    for (var i = 0; i < it.elements.length; i++) {
      if (it.elements[i].type === 'highlight') pct = it.elements[i].pct;
    }
    var headline = text(el.getAttribute('data-bar-copy-expired'));
    /* no copy for it means the theme did not ship this state — absence, not
       a bare figure on an empty strip */
    if (!headline) { ended = null; blank(el); return; }
    setAttr(el, 'data-bar-band', 'entitled');
    setAttr(el, 'data-bar-source', 'session');
    setAttr(el, 'data-bar-elements', pct ? 'highlight headline' : 'headline');
    setAttr(el, 'data-bar-state', 'expired');
    el.removeAttribute('data-bar-urgent');
    el.removeAttribute('data-bar-empty');
    el.removeAttribute('data-bar-accent');
    paintArt(el, '');
    paintDir(el, headline);
    for (i = 0; i < RENDER_ZONES.length; i++) {
      var name = RENDER_ZONES[i];
      var node = zone(el, name);
      if (!node) continue;
      if (name === 'highlight' && pct) { setText(node, pct); node.hidden = false; }
      else if (name === 'headline') { setText(node, headline); node.hidden = false; }
      else clearZone(name, node);
    }
    /* the stamp then the headline, like every drop bar state: the figure
       leads, the closing line follows it */
    reorder(el, pct ? ['highlight', 'headline'] : ['headline']);
    var live = zone(el, 'live');
    if (live) live.hidden = true;
    var tag = zone(el, 'tag');
    if (tag) tag.hidden = false;
    var saved = zone(el, 'saved');
    if (saved) saved.hidden = true;
    var announce = (curId !== 'ended:' + it.id);
    curId = 'ended:' + it.id;
    curBand = 'entitled';
    curTypes = 'headline';
    consume(it); /* said once */
    if (announce) speak({ a11y_label: (pct ? pct + ' ' : '') + headline, elements: [] });
  }

  /* Derived from end_at at render time — never a counter that ticks itself.
     A self-ticking counter drifts, comes back wrong from a frozen tab, and
     can run negative; this one goes to expired at zero and stops. */
  var RTL_SCRIPT = /[\u0590-\u05FF\u0600-\u06FF]/;
  function paintDir(el, copy) {
    var dir = RTL_SCRIPT.test(text(copy)) ? 'rtl' : 'ltr';
    setAttr(el, 'dir', dir);
    setAttr(el, 'data-bar-dir', dir);
  }

  /* the art layer: one CSS custom property on the zone, the mask lives in
     drops.css. Hidden (and its url cleared) when the item has none, so a
     campaign never inherits the previous campaign's picture. */
  function paintArt(el, src) {
    var a = zone(el, 'art');
    if (!a) return;
    if (src) {
      var was = a.getAttribute && a.getAttribute('data-bar-art-src');
      if (a.style && a.style.setProperty) a.style.setProperty('--art', 'url("' + src + '")');
      if (a.setAttribute) a.setAttribute('data-bar-art-src', src);
      /* a NEW picture enters; the same picture repainting does not re-animate */
      if (was !== src && a.classList) { a.classList.remove('is-enter'); void a.offsetWidth; a.classList.add('is-enter'); }
      a.hidden = false;
    } else {
      if (a.removeAttribute) a.removeAttribute('data-bar-art-src');
      if (a.classList) a.classList.remove('is-enter');
      if (a.style && a.style.removeProperty) a.style.removeProperty('--art');
      a.hidden = true;
    }
  }

  /* the four cells of the countdown: days, hours, minutes, seconds. The
     string form stays in the value node — the urgent predicate and the ticket
     read that, and a client with the older markup still gets a clock. */
  function paintCells(t, ms) {
    var cells = t.querySelectorAll ? t.querySelectorAll('[data-bar-cd]') : null;
    if (!cells || !cells.length) return;
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    var v = { d: d, h: h, m: m, s: s };
    for (var i = 0; i < cells.length; i++) {
      var k = cells[i].getAttribute('data-bar-cd');
      var next = pad(v[k] || 0);
      if (cells[i].textContent !== next) {
        cells[i].textContent = next;
        /* the flip: a class the CSS animates, re-armed per change */
        if (cells[i].classList) { cells[i].classList.remove('is-tick'); void cells[i].offsetWidth; cells[i].classList.add('is-tick'); }
      }
    }
  }

  function paintTimer(t, it, nowMs, label) {
    if (!t) return;
    /* a per-item label replaces the section's default wording only when the
       element carries one; the default is server-rendered and left alone */
    var l = t.querySelector && t.querySelector('[data-bar-timer-label]');
    if (l) {
      if (!l.hasAttribute('data-bar-timer-default')) l.setAttribute('data-bar-timer-default', l.textContent || '');
      setText(l, label || l.getAttribute('data-bar-timer-default') || '');
    }
    /* THE INVARIANT: a timer element carries no time of its own, so this reads
       the ITEM's window.end_at — the same field compose uses to hide the item.
       One source of time means the clock on screen cannot disagree with the
       gate that decides whether the item is on screen at all. */
    var end = parseTime(it.window ? it.window.end_at : null);
    var s = countdown(it.window ? it.window.end_at : null, nowMs);
    /* the zone may carry a label ("closes in") beside the figure; the figure
       goes into the value child when there is one, so the label survives */
    var v = (t.querySelector && t.querySelector('[data-bar-timer-value]')) || t;
    if (s === null) { setText(v, ''); t.hidden = true; return; }
    setText(v, s);
    if (end !== null) paintCells(t, end - nowMs);
    t.hidden = false;
  }

  /* THE ONE COUNTDOWN, and the reason it is EXPORTED rather than private: the
     drop ticket in the popup and the cart shows the same close time, and a
     second surface that formats its own is a second answer to the same
     question. Returns null at or past zero, so "at zero it renders as gone,
     never as a negative number" is a property of this function rather than
     something every caller has to remember. */
  function countdown(endAt, nowMs) {
    var end = parseTime(endAt);
    if (end === null) return null;
    var d = end - nowMs;
    return (d <= 0) ? null : hhmmss(d);
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function hhmmss(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    return (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
  }

  /* One second-resolution re-render, alive only while something is STILL
     counting — an end_at in the future. A merely parseable end_at is not that:
     an item whose deadline has passed used to hold the interval open forever,
     which made the sentence above false. reap() plus the `> nowMs` test make
     it true. */
  function retime(items, now) {
    var need = false;
    for (var i = 0; i < items.length; i++) {
      var w = items[i].window;
      var end = w ? parseTime(w.end_at) : null;
      var start = w ? parseTime(w.start_at) : null;
      if ((end !== null && end > now) || (start !== null && start > now)) { need = true; break; }
    }
    if (need && !ticker) ticker = setInterval(render, 1000);
    else if (!need && ticker) { clearInterval(ticker); ticker = null; }
  }

  /* ------------------------------------------------------------
     Announcements. The visible strip is NOT the live region: it holds a
     countdown that changes every second, and a live region wrapped around
     that would narrate the clock. The live region is a separate
     visually-hidden node written ONLY on the two transitions that earn an
     interruption — an entitled arrival, and live -> applied. Rotation, when
     it lands, is decorative and will never reach this function.
     ------------------------------------------------------------ */
  function hasType(sig, type) { return (' ' + sig + ' ').indexOf(' ' + type + ' ') !== -1; }

  function shouldAnnounce(it) {
    if (it.band === 'entitled' && curBand !== 'entitled') return true;
    /* live -> applied: the item already on screen has just gained the
       server's record that the discount is on the order */
    if (it.id === curId && !curApplied && isApplied(it)) return true;
    return false;
  }

  /* ONE live region for the whole drops surface — the strip's. A second one is
     a second thing to keep polite, and two regions racing is how a customer
     gets talked over. Cleared first, because an identical string written twice
     is not re-announced. */
  function announce(msg) {
    if (!msg) return;
    var region = document.querySelector('[data-bar-a11y]');
    if (!region) return;
    region.textContent = '';
    setTimeout(function () { region.textContent = msg; }, 60);
  }

  function speak(it) {
    var msg = it.a11y_label;
    if (!msg) {
      /* fall back to the item's own visible content, in element order */
      var parts = [];
      for (var i = 0; i < it.elements.length; i++) {
        var e = it.elements[i];
        if (e.type === 'highlight' && e.pct) parts.push(e.pct);
        else if (e.type === 'headline' && e.text) parts.push(e.text);
        else if (e.type === 'code' && e.value) parts.push(e.value);
      }
      msg = parts.join(' ');
    }
    announce(msg);
  }

  /* ============================================================
     boot — read the section's ambient blob, then drain the queue
     ============================================================ */

  /* IF IT CATCHES, IT COUNTS AND EMITS — same channel as drops.js, so an
     operator sees one attribute whichever half broke. */
  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }

  var SSR_ZONES = ['highlight', 'headline', 'code', 'cta', 'timer', 'live'];

  /* what the section server-rendered, captured before the island touches it.
     Only taken when the ambient blob is unreadable — that is the one case
     where the island has no item to render but the DOM already holds the
     right answer. */
  function snapshotSSR(el) {
    if (!el) return null;
    var snap = { zones: [] };
    for (var i = 0; i < SSR_ZONES.length; i++) {
      var n = zone(el, SSR_ZONES[i]);
      if (n) snap.zones.push({ node: n, text: n.textContent, hidden: n.hidden,
        isLink: SSR_ZONES[i] === 'cta', href: n.getAttribute('href') });
    }
    snap.band = el.getAttribute('data-bar-band');
    snap.elements = el.getAttribute('data-bar-elements');
    snap.source = el.getAttribute('data-bar-source');
    return snap;
  }
  function restoreSSR(el) {
    if (!ssrSnapshot) return;
    for (var i = 0; i < ssrSnapshot.zones.length; i++) {
      var z = ssrSnapshot.zones[i];
      setText(z.node, z.text);
      z.node.hidden = z.hidden;
      if (z.isLink) setAttr(z.node, 'href', z.href);
    }
    setAttr(el, 'data-bar-band', ssrSnapshot.band);
    setAttr(el, 'data-bar-elements', ssrSnapshot.elements);
    setAttr(el, 'data-bar-source', ssrSnapshot.source);
    el.removeAttribute('data-bar-empty');
    curId = null;
    curBand = null;
    curTypes = '';
    curApplied = false;
  }

  /* ------------------------------------------------------------
     THE CODE ELEMENT. The code is CONTENT and copying is an enhancement, so
     there is no path that leaves a dead button: the button's own text is the
     literal code, it is selectable, and when the clipboard is unavailable or
     refuses, the tap SELECTS that text instead. A refusal is not swallowed —
     it emits, like every other catch here.
     ------------------------------------------------------------ */
  function selectText(node) {
    try {
      var r = document.createRange();
      r.selectNodeContents(node);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
      return true;
    } catch (e) { return false; }
  }

  function markCopied(node) {
    node.setAttribute('data-bar-copied', '');
    setTimeout(function () { try { node.removeAttribute('data-bar-copied'); } catch (e) {} }, 1600);
  }

  /* Generic, and exported, because THREE nodes now want this behaviour — the
     strip's code chip, the drop ticket's, and the cart badge's. A copy handler
     re-typed per surface is three chances to lose the refusal path that is the
     only reason the button is never dead. Idempotent: binding twice is a
     no-op, so a re-render can call it without stacking listeners. */
  function bindCopy(node) {
    if (!node || !node.addEventListener || node.__exonCopyBound) return;
    node.__exonCopyBound = true;
    node.addEventListener('click', function () {
      var value = node.textContent || '';
      if (!value) return;
      var clip = window.navigator && navigator.clipboard;
      if (!clip || !clip.writeText) { selectText(node); return; }
      try {
        clip.writeText(value).then(function () {
          markCopied(node);
          /* The glyph swapping to a tick is the SIGHTED confirmation. An
             asynchronous success that only changes a mask tells a screen-reader
             user nothing at all — and unlike the refusal path, which selects
             the text and is therefore discoverable, success leaves no trace to
             find. The wording rides on the node so it stays in Liquid with the
             rest of the copy; no attribute, no announcement, rather than a
             string invented here. */
          announce(node.getAttribute('data-copied-label'));
        }, function (e) {
          fault('code_copy_refused', e);
          selectText(node);
        });
      } catch (e) {
        fault('code_copy_failed', e);
        selectText(node);
      }
    });
  }

  function bindCode() {
    var el = root();
    bindCopy(el && zone(el, 'code'));
  }

  function readAmbient() {
    var el = document.getElementById('exon-bar-ambient');
    /* ABSENT is a normal configuration, not a fault: the section omits the
       blob when there is no ambient copy, and marks the shell empty itself. */
    if (!el || !el.textContent) return;
    var item = null;
    try {
      item = JSON.parse(el.textContent);
    } catch (e) {
      /* MALFORMED is different. The registry would be left empty and the
         island would then blank correct server-rendered copy — so degrade to
         leaving the SSR content exactly where it is, and say so out loud. */
      fault('ambient_parse_failed', e);
      ssrHeld = true;
      ssrSnapshot = snapshotSSR(root());
      return;
    }
    /* an ARRAY registers every item (the section emits its campaign blocks
       plus the fallback in one blob); a single object is the older form */
    if (Array.isArray(item)) { for (var i = 0; i < item.length; i++) if (item[i]) register(item[i]); }
    else if (item) register(item);
  }

  function install() {
    var b = (window.EXON.bar = window.EXON.bar || { q: [] });
    var pending = Array.isArray(b.q) ? b.q : [];

    b.register = register;
    b.withdraw = withdraw;
    b.validate = validate;
    b.compose = compose;
    b.items = values;
    /* the two pieces a SECOND drops surface needs from this island, published
       on the same object it already reads its items from — so a consumer has
       one dependency to check for rather than three. Their presence is also
       the signal that the island installed at all: no install, no items, no
       ticket, which is the correct degradation either way. */
    b.countdown = countdown;
    b.bindCopy = bindCopy;
    /* the one expired item this page may show, or null — the ticket reads
       it so both surfaces close on the same fact, from the same memory */
    b.ended = function () { return ended; };
    b.q = [];

    for (var i = 0; i < pending.length; i++) {
      var op = pending[i];
      if (!op) continue;
      if (op[0] === 'register') register(op[1]);
      else if (op[0] === 'withdraw') withdraw(op[1]);
    }
  }

  /* ------------------------------------------------------------
     "have drops settled?" — the gate on collapsing an empty shell.
     Settled means the drops client reached a terminal path (it emits
     exon:drops:state on every one, successes and failures alike) AND, when it
     started the /public read, that read's FIRST answer is in (it reports
     publicPending until then and emits exon:drops:public once, after the
     campaigns are registered). Without the second half a visitor's session
     settled synchronously, the shell collapsed for good, and a campaign that
     answered a moment later was never painted. A client with no
     publicPending settles on its state alone, as before. The backstop forces
     settlement whatever is still out; it covers drops.js never loading or
     never running and is sized past that client's own worst case — two
     4500ms attempts plus the retry delay — so it never fires ahead of a
     request that is still live.
     ------------------------------------------------------------ */
  var SETTLE_BACKSTOP_MS = 11000;
  var dropsFinished = false;
  /* force is the backstop's alone; listeners pass an Event, never true */
  function markSettled(force) {
    if (dropsSettled) return;
    if (force !== true) {
      if (!dropsFinished) return;
      var dp = window.EXON.drops;
      if (dp && dp.publicPending === true) return;
    }
    dropsSettled = true;
    /* the session settled: if no entitled item is on offer and a campaign
       this browser held ended within the bound, say so — once */
    try {
      var d = window.EXON.drops;
      var entitledNow = !!(d && d.state && d.state.entitled);
      if (!entitledNow && !ended) ended = recallEnded(Date.now());
    } catch (e) { fault('ended_recall_failed', e); }
    schedule();
  }

  /* ambient FIRST: the section already painted its settings server-side, and
     an empty registry would blank that content before the blob is read */
  readAmbient();
  install();
  bindCode();
  bindRoll();

  function onDropsState() { dropsFinished = true; markSettled(); }
  /* both listeners and the backstop always, so neither script's load order
     nor which half lands first can strand the gate open or shut. "Finished
     before we booted" is read from a PUBLISHED state (drops.js sets
     EXON.drops.state only in publishState, first reached from its terminal
     finish), never from the API object existing: drops.js exposes that
     object before its session request answers, and a member's entitlement
     arriving after a collapse could never be painted. */
  document.addEventListener('exon:drops:public', markSettled);
  document.addEventListener('exon:drops:state', onDropsState);
  if (window.EXON.drops && window.EXON.drops.state) onDropsState();
  setTimeout(function () { markSettled(true); }, SETTLE_BACKSTOP_MS);

  schedule();

  /* exported for direct testing of the two pure stages */
  window.ExonDropsBar = {
    validate: validate,
    compose: compose,
    register: register,
    withdraw: withdraw,
    items: values,
    countdown: countdown,
    bindCopy: bindCopy,
    ended: function () { return ended; }
  };
})();
